import React, { useEffect, useState } from 'react';
import api from '../api';
import DrillDownModal from '../components/DrillDownModal';
import AnnualReportStudio from '../components/AnnualReportStudio';
import EvidenceHub from '../components/EvidenceHub';
import ObjectiveAuthor from '../components/ObjectiveAuthor';
import {
  Leaf, Lightning, Drop, Recycle, ShoppingCart, Shield, Coin, Users, TreeEvergreen,
  Target, CheckCircle, Warning, Clock, ArrowRight, Robot, SpinnerGap, CaretDown, CaretUp,
  ChartBar, Factory, CurrencyCircleDollar, MagnifyingGlass, Lightbulb, FileText
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, PieChart, Pie, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const STATUS_COLORS = { Green: '#2D7D46', Amber: '#D9943B', Red: '#C6493A' };
const STATUS_BG = { Green: 'bg-status-on-track-bg', Amber: 'bg-status-watch-bg', Red: 'bg-status-alert-bg' };
const STATUS_TEXT = { Green: 'text-status-on-track', Amber: 'text-status-watch', Red: 'text-status-alert' };
const TAB_ICONS = {
  scorecard: ChartBar, facilities: Factory, procurement: ShoppingCart,
  resilience: Shield, reserves: CurrencyCircleDollar, report: Target,
  evidence: Shield, author: Lightbulb
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase ${STATUS_BG[status] || 'bg-gray-100'} ${STATUS_TEXT[status] || 'text-gray-600'}`}>
      {status}
    </span>
  );
}

function KpiBar({ kpi, onClick }) {
  const pct = typeof kpi.current === 'number' && typeof kpi.target === 'number' && kpi.target !== 0
    ? Math.min(100, Math.round((kpi.current / kpi.target) * 100)) : null;
  return (
    <div data-testid={`kpi-${kpi.name}`} onClick={onClick} className="bg-white border border-border-default rounded-lg p-3 hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-pointer group">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-semibold text-content-secondary truncate flex-1">{kpi.name}</p>
        <StatusBadge status={kpi.status} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg font-heading font-bold text-content-primary">{typeof kpi.current === 'number' ? kpi.current.toLocaleString() : kpi.current}</span>
        <span className="text-[9px] text-content-disabled">{kpi.unit}</span>
      </div>
      {pct !== null && (
        <div className="mt-1.5">
          <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: STATUS_COLORS[kpi.status] || '#87A98E' }} />
          </div>
          <div className="flex justify-between mt-0.5 text-[8px] text-content-disabled">
            <span>Baseline: {kpi.baseline}</span>
            <span>Target: {kpi.target}</span>
          </div>
        </div>
      )}
      <p className="text-[8px] text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity mt-1">Click to drill down</p>
    </div>
  );
}

function ObjectiveCard({ obj, onClick, onDrill }) {
  const greenKpis = (obj.kpis || []).filter(k => k.status === 'Green').length;
  const totalKpis = (obj.kpis || []).length;
  return (
    <div data-testid={`obj-${obj.objective_id}`} className="bg-white border border-border-default rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all group">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[9px] font-mono text-content-disabled">{obj.objective_id}</span>
            <StatusBadge status={obj.status} />
            <span className="text-[8px] text-content-disabled">{obj.category}</span>
          </div>
          <h3 className="text-sm font-heading font-semibold text-content-primary leading-tight">{obj.title}</h3>
          <p className="text-[10px] text-content-secondary mt-0.5">{obj.owner} | {obj.scope}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2.5 text-[9px]">
        <span className="text-status-on-track font-semibold">{greenKpis}/{totalKpis} KPIs on target</span>
        <span className="text-content-disabled">{obj.baseline_year}–{obj.target_year}</span>
      </div>
      {obj.ai_insight && (
        <div className="mt-2 p-2 bg-brand-accent/5 rounded border border-brand-accent/10">
          <div className="flex items-start gap-1.5">
            <Robot size={10} className="text-brand-accent mt-0.5 flex-shrink-0" />
            <p className="text-[9px] text-content-secondary leading-relaxed italic">{obj.ai_insight}</p>
          </div>
        </div>
      )}
      <button data-testid={`view-kpi-${obj.objective_id}`} onClick={onClick} className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-brand-primary/5 text-brand-primary border border-brand-primary/15 rounded-lg text-[10px] font-semibold hover:bg-brand-primary/10 transition-all">
        <ChartBar size={12} /> View KPIs & Strategies
      </button>
    </div>
  );
}

// ====== Objective Detail Drill-Down Panel ======

function ObjectiveDetailPanel({ objectiveId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeKpi, setActiveKpi] = useState(null);

  useEffect(() => {
    if (!objectiveId) return;
    setLoading(true);
    api.get(`/api/env/objective/${objectiveId}`).then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [objectiveId]);

  if (loading) return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-10 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-xl p-8 text-center" onClick={e => e.stopPropagation()}>
        <SpinnerGap size={24} className="animate-spin text-brand-primary mx-auto" />
        <p className="text-sm text-content-secondary mt-2">Loading objective details...</p>
      </div>
    </div>
  );

  if (!data) return null;

  const obj = data.objective;
  const kpiHistory = data.kpi_history || [];
  const evidence = data.evidence_summary || {};
  const relatedActions = data.related_actions || [];
  const relatedMetrics = data.related_metrics || [];

  // Group KPI history by KPI name
  const kpiNames = [...new Set(kpiHistory.map(h => h.kpi_name))];
  const kpiTrendData = {};
  kpiNames.forEach(name => {
    kpiTrendData[name] = kpiHistory.filter(h => h.kpi_name === name).sort((a, b) => a.period.localeCompare(b.period));
  });

  // Get matching KPI definition from objective
  const getKpiDef = (name) => (obj.kpis || []).find(k => k.name === name);

  // Colors for KPI lines
  const kpiColors = ['#0A4D3C', '#C2714F', '#D9943B', '#5B7FBF', '#87A98E', '#8B5CF6'];

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center pt-6 pb-6 overflow-y-auto animate-fade-in" onClick={onClose}>
      <div data-testid={`obj-detail-${objectiveId}`} className="bg-surface-default rounded-xl w-full max-w-5xl mx-4 shadow-xl animate-slide-up" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 bg-brand-primary rounded-t-xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-white/60">{obj.objective_id}</span>
                <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${obj.status === 'Green' ? 'bg-emerald-400/20 text-emerald-200' : obj.status === 'Red' ? 'bg-red-400/20 text-red-200' : 'bg-amber-400/20 text-amber-200'}`}>{obj.status}</span>
                <span className="text-[9px] text-white/40 capitalize">{obj.category}</span>
              </div>
              <h2 className="font-heading font-semibold text-lg text-white">{obj.title}</h2>
              <p className="text-[11px] text-white/60 mt-0.5">{obj.owner} | {obj.scope} | {obj.baseline_year}–{obj.target_year}</p>
            </div>
            <button data-testid="close-obj-detail" onClick={onClose} className="text-white/60 hover:text-white text-lg font-bold px-2">×</button>
          </div>
          {obj.rationale && <p className="text-[10px] text-white/50 mt-2 leading-relaxed">{obj.rationale}</p>}
        </div>

        <div className="p-6 space-y-5">
          {/* KPI Cards */}
          <div>
            <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Key Performance Indicators ({(obj.kpis || []).length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {(obj.kpis || []).map((kpi, i) => {
                const isActive = activeKpi === kpi.name;
                const trendData = kpiTrendData[kpi.name] || [];
                const latestTrend = trendData.length > 1 ? trendData[trendData.length - 1].value - trendData[trendData.length - 2].value : null;
                const pct = typeof kpi.current === 'number' && typeof kpi.target === 'number' && kpi.target !== 0
                  ? Math.min(100, Math.round(Math.abs(kpi.current - kpi.baseline) / Math.abs(kpi.target - kpi.baseline) * 100)) : null;
                return (
                  <button key={i} data-testid={`detail-kpi-${kpi.name.replace(/\s+/g, '-')}`}
                    onClick={() => setActiveKpi(isActive ? null : kpi.name)}
                    className={`text-left p-3 rounded-lg border transition-all ${isActive ? 'border-brand-primary bg-brand-primary/5 shadow-sm' : 'border-border-default bg-white hover:border-brand-primary/30'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <StatusBadge status={kpi.status} />
                      {latestTrend != null && (
                        <span className={`text-[9px] font-mono font-bold ${latestTrend > 0 ? 'text-emerald-600' : latestTrend < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {latestTrend > 0 ? '+' : ''}{latestTrend.toFixed(1)}
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-heading font-bold text-content-primary">{typeof kpi.current === 'number' ? kpi.current.toLocaleString() : kpi.current}</p>
                    <p className="text-[9px] text-content-secondary font-medium truncate">{kpi.name}</p>
                    <p className="text-[8px] text-content-disabled">{kpi.unit}</p>
                    {pct !== null && (
                      <div className="mt-1.5">
                        <div className="h-1 bg-surface-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: STATUS_COLORS[kpi.status] || '#87A98E' }} />
                        </div>
                        <div className="flex justify-between mt-0.5 text-[7px] text-content-disabled">
                          <span>B: {kpi.baseline}</span>
                          <span>T: {kpi.target}</span>
                        </div>
                      </div>
                    )}
                    {kpi.data_source && <p className="text-[7px] text-content-disabled mt-1">Source: {kpi.data_source}</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* KPI Trend Chart */}
          {kpiNames.length > 0 && (
            <div className="bg-white border border-border-default rounded-lg p-4">
              <h3 className="font-heading font-semibold text-sm text-content-primary mb-1">
                KPI Trends — Quarterly (2024–2026)
              </h3>
              <p className="text-[9px] text-content-disabled mb-3">
                {activeKpi ? `Showing: ${activeKpi}` : 'Showing all KPIs — click a KPI card above to focus'}
              </p>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8ECE9" />
                  <XAxis dataKey="period" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 9, fill: '#49564C' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#49564C' }} />
                  <Tooltip contentStyle={{ fontSize: '11px' }} />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  {kpiNames
                    .filter(name => !activeKpi || name === activeKpi)
                    .map((name, i) => (
                      <Line key={name} data={kpiTrendData[name]} dataKey="value" name={name}
                        stroke={kpiColors[i % kpiColors.length]} strokeWidth={activeKpi === name ? 3 : 1.5}
                        dot={{ r: activeKpi === name ? 4 : 2 }} connectNulls />
                    ))
                  }
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Strategies & Measures */}
          {(obj.strategies || []).length > 0 && (
            <div className="bg-white border border-border-default rounded-lg p-4">
              <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Strategies & Measures ({obj.strategies.length})</h3>
              <div className="space-y-2">
                {obj.strategies.map((s, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-surface-default rounded-lg border border-border-subtle">
                    <div className="w-7 h-7 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target size={14} className="text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-content-primary">{s.name}</p>
                      <p className="text-[10px] text-content-secondary mt-0.5">{s.measure}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[9px] text-content-disabled">
                        <span className="flex items-center gap-1"><Clock size={9} /> {s.milestone}</span>
                        <span className="flex items-center gap-1"><CurrencyCircleDollar size={9} /> {s.budget}</span>
                        <span>{s.office}</span>
                      </div>
                      <p className="text-[9px] text-brand-primary mt-1 font-medium">Expected: {s.outcome}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evidence & Governance Actions (side by side) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Evidence Summary */}
            <div className="bg-white border border-border-default rounded-lg p-4">
              <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Evidence Status</h3>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1">
                  <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${evidence.completeness_pct}%` }} />
                  </div>
                </div>
                <span className="text-sm font-heading font-bold text-brand-primary">{evidence.completeness_pct}%</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="p-2 bg-surface-default rounded-lg">
                  <p className="text-lg font-heading font-bold text-content-primary">{evidence.count}</p>
                  <p className="text-[9px] text-content-disabled">Documents Uploaded</p>
                </div>
                <div className="p-2 bg-surface-default rounded-lg">
                  <p className="text-lg font-heading font-bold text-content-primary">{evidence.expected}</p>
                  <p className="text-[9px] text-content-disabled">Expected Documents</p>
                </div>
              </div>
            </div>

            {/* Related Actions */}
            {relatedActions.length > 0 && (
              <div className="bg-white border border-border-default rounded-lg p-4">
                <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Related Governance Actions</h3>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {relatedActions.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-surface-default rounded-lg text-[10px]">
                      <StatusBadge status={a.status === 'Overdue' ? 'Red' : a.status === 'Open' ? 'Amber' : 'Green'} />
                      <div className="flex-1 min-w-0">
                        <p className="text-content-primary truncate font-medium">{a.description}</p>
                        <p className="text-content-disabled">{a.owner} · Due: {a.due_date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Facility Metrics (for facilities objectives) */}
            {relatedActions.length === 0 && relatedMetrics.length > 0 && (
              <div className="bg-white border border-border-default rounded-lg p-4">
                <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Latest Facility Readings</h3>
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {relatedMetrics.slice(0, 8).map((mv, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-surface-default rounded-lg text-[10px]">
                      <StatusBadge status={mv.status === 'Alert' ? 'Red' : mv.status === 'Watch' ? 'Amber' : 'Green'} />
                      <div className="flex-1 min-w-0">
                        <p className="text-content-primary truncate font-medium">{mv.metric_name} — {mv.site_name}</p>
                        <p className="text-content-disabled">{mv.period} · {mv.value?.toLocaleString()} {mv.anomaly_flag ? '(Anomaly)' : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* AI Insight */}
          {obj.ai_insight && (
            <div className="p-3 bg-brand-accent/5 rounded-lg border border-brand-accent/10 flex items-start gap-2">
              <Robot size={14} className="text-brand-accent mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-semibold text-brand-accent uppercase mb-0.5">AI Insight</p>
                <p className="text-xs text-content-secondary italic">{obj.ai_insight}</p>
              </div>
            </div>
          )}

          {/* Site Coverage */}
          {(obj.site_coverage || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] text-content-disabled font-semibold uppercase">Site Coverage:</span>
              {obj.site_coverage.map((s, i) => (
                <span key={i} className="px-2 py-0.5 bg-surface-muted rounded text-[9px] text-content-secondary font-mono">{s}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ====== Tab Components ======

function ScorecardTab({ objectives, summary, onDrill }) {
  const statusData = [
    { name: 'Green', value: summary.green, fill: STATUS_COLORS.Green },
    { name: 'Amber', value: summary.amber, fill: STATUS_COLORS.Amber },
    { name: 'Red', value: summary.red, fill: STATUS_COLORS.Red },
  ].filter(d => d.value > 0);
  const categoryData = {};
  objectives.forEach(o => { categoryData[o.category] = (categoryData[o.category] || 0) + 1; });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Objectives', val: summary.total, color: 'text-brand-primary', dk: 'env_all_objectives' },
          { label: 'On Target (Green)', val: summary.green, color: 'text-status-on-track', dk: 'env_all_objectives' },
          { label: 'Watch (Amber)', val: summary.amber, color: 'text-status-watch', dk: 'env_all_objectives' },
          { label: 'Critical (Red)', val: summary.red, color: 'text-status-alert', dk: 'env_all_objectives' },
        ].map((k, i) => (
          <div key={i} onClick={() => onDrill(k.dk)} className="bg-white border border-border-default rounded-lg p-3.5 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all group">
            <p className={`text-2xl font-heading font-bold ${k.color}`}>{k.val}</p>
            <p className="text-[10px] text-content-secondary font-medium">{k.label}</p>
            <p className="text-[8px] text-brand-accent opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">Click to drill down</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Objective Status Overview</h3>
          <div className="space-y-2">
            {objectives.map(o => (
              <div key={o.objective_id} onClick={() => onDrill(`env_obj_${o.objective_id}`)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-muted cursor-pointer transition-colors">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[o.status] }} />
                <span className="text-[10px] font-mono text-content-disabled w-14 flex-shrink-0">{o.objective_id}</span>
                <span className="text-xs text-content-primary flex-1 truncate">{o.title}</span>
                <StatusBadge status={o.status} />
                <span className="text-[9px] text-content-disabled">{(o.kpis || []).filter(k => k.status === 'Green').length}/{(o.kpis || []).length}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} strokeWidth={2} stroke="#fff">
                {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Tooltip contentStyle={{ fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1.5">
            <h4 className="text-[10px] font-semibold text-content-secondary uppercase">Top AI Insights</h4>
            {objectives.filter(o => o.ai_insight).slice(0, 3).map(o => (
              <div key={o.objective_id} className="flex items-start gap-1.5 text-[9px]">
                <Robot size={10} className="text-brand-accent mt-0.5 flex-shrink-0" />
                <span className="text-content-secondary"><b>{o.objective_id}:</b> {o.ai_insight.slice(0, 80)}...</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FacilitiesTab({ data, onDrill }) {
  if (!data) return <div className="text-center py-8 text-content-disabled">Loading...</div>;
  const obj = data.utilities_objective;
  const wasteObj = data.waste_objective;
  const kpiHistory = data.kpi_history || [];
  const elecData = kpiHistory.filter(k => k.kpi_name === 'Electricity Index');
  const waterData = kpiHistory.filter(k => k.kpi_name === 'Water Index');
  const wasteData = kpiHistory.filter(k => k.kpi_name === 'Waste Index');
  return (
    <div className="space-y-5">
      {obj && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(obj.kpis || []).map((k, i) => <KpiBar key={i} kpi={k} onClick={() => onDrill(`env_obj_${obj.objective_id}`)} />)}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Utilities Index Trend (2019 = 100)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="period" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} domain={[40, 100]} />
              <Tooltip contentStyle={{ fontSize: '11px' }} />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
              <Line data={elecData} dataKey="value" name="Electricity" stroke="#D9943B" strokeWidth={2} dot={{ r: 3 }} />
              <Line data={waterData} dataKey="value" name="Water" stroke="#2D7D46" strokeWidth={2} dot={{ r: 3 }} />
              <Line data={wasteData} dataKey="value" name="Waste" stroke="#87A98E" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Site Performance Ranking</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={(data.sites || []).map(s => ({ name: s.name.replace('BSP ', '').slice(0, 10), elec: Math.abs(s.electricity_change_pct), water: Math.abs(s.water_change_pct), waste: Math.abs(s.waste_change_pct) }))} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 9 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={70} />
              <Tooltip contentStyle={{ fontSize: '11px' }} formatter={v => [`${v}% reduction`]} />
              <Bar dataKey="elec" name="Electricity" fill="#D9943B" barSize={6} />
              <Bar dataKey="water" name="Water" fill="#2D7D46" barSize={6} />
              <Legend wrapperStyle={{ fontSize: '9px' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {wasteObj && (
        <div>
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Waste & Circularity KPIs</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(wasteObj.kpis || []).map((k, i) => <KpiBar key={i} kpi={k} onClick={() => onDrill(`env_obj_${wasteObj.objective_id}`)} />)}
          </div>
        </div>
      )}
      {obj?.ai_insight && (
        <div className="p-3 bg-brand-accent/5 rounded-lg border border-brand-accent/10 flex items-start gap-2">
          <Robot size={14} className="text-brand-accent mt-0.5 flex-shrink-0" />
          <p className="text-xs text-content-secondary italic">{obj.ai_insight}</p>
        </div>
      )}
    </div>
  );
}

function ProcurementTab({ data, onDrill }) {
  if (!data) return <div className="text-center py-8 text-content-disabled">Loading...</div>;
  const obj = data.objective;
  return (
    <div className="space-y-5">
      {obj && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(obj.kpis || []).map((k, i) => <KpiBar key={i} kpi={k} onClick={() => onDrill(`env_obj_${obj.objective_id}`)} />)}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Procurement Records</h3>
          <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0"><tr className="bg-surface-muted">
                {['Tender', 'Category', 'Supplier', 'Green Criteria', 'Status'].map(h => <th key={h} className="text-left px-2 py-1.5 font-semibold text-content-secondary text-[9px] uppercase">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-border-subtle">
                {(data.procurement_records || []).map((r, i) => (
                  <tr key={i} className="hover:bg-surface-muted/50">
                    <td className="px-2 py-1.5">{r.tender_id}</td>
                    <td className="px-2 py-1.5">{r.category}</td>
                    <td className="px-2 py-1.5">{r.supplier}</td>
                    <td className="px-2 py-1.5">{r.green_criteria_included ? <CheckCircle size={12} className="text-status-on-track" /> : <Warning size={12} className="text-status-watch" />}</td>
                    <td className="px-2 py-1.5"><StatusBadge status={r.award_status === 'Awarded' ? 'Green' : 'Amber'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Supplier ESG Status</h3>
          <div className="space-y-2">
            {(data.suppliers || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-surface-default rounded-lg">
                <div>
                  <p className="text-xs font-semibold text-content-primary">{s.name}</p>
                  <p className="text-[9px] text-content-disabled">{s.category} | Risk: {s.risk_level || 'Medium'}</p>
                </div>
                <StatusBadge status={s.esg_score >= 75 ? 'Green' : s.esg_score >= 50 ? 'Amber' : 'Red'} />
              </div>
            ))}
          </div>
        </div>
      </div>
      {obj?.ai_insight && (
        <div className="p-3 bg-brand-accent/5 rounded-lg border border-brand-accent/10 flex items-start gap-2">
          <Robot size={14} className="text-brand-accent mt-0.5 flex-shrink-0" />
          <p className="text-xs text-content-secondary italic">{obj.ai_insight}</p>
        </div>
      )}
    </div>
  );
}

function ResilienceTab({ data, onDrill }) {
  if (!data) return <div className="text-center py-8 text-content-disabled">Loading...</div>;
  const emsObj = data.ems_objective;
  const resObj = data.resilience_objective;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {emsObj && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[9px] font-mono text-content-disabled">{emsObj.objective_id}</span>
              <h3 className="font-heading font-semibold text-sm text-content-primary">{emsObj.title}</h3>
              <StatusBadge status={emsObj.status} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(emsObj.kpis || []).map((k, i) => <KpiBar key={i} kpi={k} onClick={() => onDrill(`env_obj_${emsObj.objective_id}`)} />)}
            </div>
          </div>
        )}
        {resObj && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[9px] font-mono text-content-disabled">{resObj.objective_id}</span>
              <h3 className="font-heading font-semibold text-sm text-content-primary">{resObj.title}</h3>
              <StatusBadge status={resObj.status} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(resObj.kpis || []).map((k, i) => <KpiBar key={i} kpi={k} onClick={() => onDrill(`env_obj_${resObj.objective_id}`)} />)}
            </div>
          </div>
        )}
      </div>
      <div className="bg-white border border-border-default rounded-lg p-4">
        <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Governance Actions</h3>
        <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0"><tr className="bg-surface-muted">
              {['Action', 'Site', 'Description', 'Owner', 'Due', 'Severity', 'Status'].map(h => <th key={h} className="text-left px-2 py-1.5 font-semibold text-content-secondary text-[9px] uppercase">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {(data.governance_actions || []).map((a, i) => (
                <tr key={i} className="hover:bg-surface-muted/50">
                  <td className="px-2 py-1.5 font-mono text-[9px]">{a.action_id}</td>
                  <td className="px-2 py-1.5">{a.site_name}</td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate">{a.description}</td>
                  <td className="px-2 py-1.5">{a.owner}</td>
                  <td className="px-2 py-1.5">{a.due_date}</td>
                  <td className="px-2 py-1.5">{a.severity}</td>
                  <td className="px-2 py-1.5"><StatusBadge status={a.status === 'Overdue' ? 'Red' : a.status === 'Open' ? 'Amber' : 'Green'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[emsObj, resObj].filter(Boolean).map(o => o.ai_insight && (
          <div key={o.objective_id} className="p-3 bg-brand-accent/5 rounded-lg border border-brand-accent/10 flex items-start gap-2">
            <Robot size={14} className="text-brand-accent mt-0.5 flex-shrink-0" />
            <p className="text-xs text-content-secondary italic"><b>{o.objective_id}:</b> {o.ai_insight}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReservesTab({ data, onDrill }) {
  if (!data) return <div className="text-center py-8 text-content-disabled">Loading...</div>;
  const sections = [
    { obj: data.reserves_objective, label: 'Responsible Reserves & Investment' },
    { obj: data.gold_objective, label: 'Responsible Gold Sourcing' },
    { obj: data.currency_objective, label: 'Sustainable Currency Lifecycle' },
    { obj: data.culture_objective, label: 'Culture, Awareness & Green Behavior' },
  ].filter(s => s.obj);
  return (
    <div className="space-y-5">
      {sections.map(({ obj, label }) => (
        <div key={obj.objective_id}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[9px] font-mono text-content-disabled">{obj.objective_id}</span>
            <h3 className="font-heading font-semibold text-sm text-content-primary">{label}</h3>
            <StatusBadge status={obj.status} />
          </div>
          <div className={`grid grid-cols-2 ${(obj.kpis || []).length > 3 ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-2`}>
            {(obj.kpis || []).map((k, i) => <KpiBar key={i} kpi={k} onClick={() => onDrill(`env_obj_${obj.objective_id}`)} />)}
          </div>
          {obj.ai_insight && (
            <div className="mt-2 p-2.5 bg-brand-accent/5 rounded-lg border border-brand-accent/10 flex items-start gap-2">
              <Robot size={12} className="text-brand-accent mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-content-secondary italic">{obj.ai_insight}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ====== AI Copilot Panel ======

function AiCopilotPanel({ objectives }) {
  const [action, setAction] = useState('analyze');
  const [objId, setObjId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const resp = await api.post('/api/env/ai-copilot', { action, objective_id: objId });
      setResult(resp.data.result);
    } catch { setResult({ summary: 'AI unavailable' }); }
    setLoading(false);
  };

  return (
    <div className="bg-white border border-border-default rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Robot size={16} className="text-brand-accent" />
        <h3 className="font-heading font-semibold text-sm text-content-primary">AI Stewardship Copilot</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <select data-testid="copilot-objective" value={objId} onChange={e => setObjId(e.target.value)} className="px-2.5 py-2 border border-border-default rounded-lg text-xs">
          <option value="">Select objective...</option>
          {objectives.map(o => <option key={o.objective_id} value={o.objective_id}>{o.objective_id}: {o.title}</option>)}
        </select>
        <select data-testid="copilot-action" value={action} onChange={e => setAction(e.target.value)} className="px-2.5 py-2 border border-border-default rounded-lg text-xs">
          <option value="analyze">Analyze & Insights</option>
          <option value="evidence_check">Evidence Completeness Check</option>
          <option value="corrective_action">Corrective Action Recommender</option>
          <option value="narrative">Draft Annual Report Narrative</option>
        </select>
        <button data-testid="copilot-run" onClick={run} disabled={loading || !objId} className="px-4 py-2 bg-brand-accent text-white rounded-lg text-xs font-semibold hover:bg-brand-accent/90 disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><SpinnerGap size={14} className="animate-spin" /> Running...</> : 'Run AI Analysis'}
        </button>
      </div>
      {result && (
        <div data-testid="copilot-result" className="mt-3 p-3 bg-surface-default rounded-lg border border-border-subtle space-y-2 text-xs">
          {result.summary && <p className="font-semibold text-content-primary">{result.summary}</p>}
          {result.narrative && <p className="text-content-secondary whitespace-pre-line">{result.narrative}</p>}
          {result.key_findings?.length > 0 && <div><p className="font-semibold text-content-secondary text-[10px] uppercase">Key Findings</p><ul className="list-disc list-inside text-content-secondary">{result.key_findings.map((f,i) => <li key={i}>{f}</li>)}</ul></div>}
          {result.risks?.length > 0 && <div><p className="font-semibold text-status-alert text-[10px] uppercase">Risks</p><ul className="list-disc list-inside text-content-secondary">{result.risks.map((r,i) => <li key={i}>{r}</li>)}</ul></div>}
          {result.recommendations?.length > 0 && <div><p className="font-semibold text-brand-primary text-[10px] uppercase">Recommendations</p><ul className="list-disc list-inside text-content-secondary">{result.recommendations.map((r,i) => <li key={i}>{r}</li>)}</ul></div>}
          {result.priority_actions?.length > 0 && <div><p className="font-semibold text-brand-primary text-[10px] uppercase">Priority Actions</p>{result.priority_actions.map((a,i) => <div key={i} className="p-2 bg-white rounded border border-border-subtle mt-1"><b>{a.action}</b> — {a.owner} | {a.timeline} | Impact: {a.expected_impact}</div>)}</div>}
          {result.missing_evidence?.length > 0 && <div><p className="font-semibold text-status-watch text-[10px] uppercase">Missing Evidence</p><ul className="list-disc list-inside text-content-secondary">{result.missing_evidence.map((e,i) => <li key={i}>{e}</li>)}</ul></div>}
          {result.trend && <p className="text-[10px] text-content-disabled">Trend: <span className="font-semibold">{result.trend}</span></p>}
        </div>
      )}
    </div>
  );
}

// ====== Main Page ======

const TABS = [
  { key: 'scorecard', label: 'Executive Scorecard' },
  { key: 'facilities', label: 'Facilities & Utilities' },
  { key: 'procurement', label: 'Procurement & Supplier' },
  { key: 'resilience', label: 'Resilience & Compliance' },
  { key: 'reserves', label: 'Reserves / Gold / Currency' },
  { key: 'report', label: 'Annual Report' },
  { key: 'evidence', label: 'Evidence Hub' },
  { key: 'author', label: 'New Objective' },
];

export default function EnvironmentalPerformance() {
  const [tab, setTab] = useState('scorecard');
  const [dashboard, setDashboard] = useState(null);
  const [facilitiesData, setFacilitiesData] = useState(null);
  const [procurementData, setProcurementData] = useState(null);
  const [resilienceData, setResilienceData] = useState(null);
  const [reservesData, setReservesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);
  const [expandedObj, setExpandedObj] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/env/dashboard').then(r => { setDashboard(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'facilities' && !facilitiesData) api.get('/api/env/facilities').then(r => setFacilitiesData(r.data));
    if (tab === 'procurement' && !procurementData) api.get('/api/env/procurement').then(r => setProcurementData(r.data));
    if (tab === 'resilience' && !resilienceData) api.get('/api/env/resilience').then(r => setResilienceData(r.data));
    if (tab === 'reserves' && !reservesData) api.get('/api/env/reserves').then(r => setReservesData(r.data));
  }, [tab]);

  if (loading) return <div className="flex items-center justify-center py-20"><SpinnerGap size={28} className="animate-spin text-brand-primary" /><span className="ml-3 text-sm text-content-secondary">Loading Environmental Performance...</span></div>;
  if (!dashboard) return <div className="text-center py-20 text-content-disabled">Failed to load data</div>;

  const { objectives, summary } = dashboard;

  return (
    <div data-testid="env-performance-page" className="space-y-5 animate-fade-in">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <TreeEvergreen size={20} className="text-brand-primary" />
          <h1 className="font-heading font-bold text-2xl text-content-primary">Environmental Performance</h1>
        </div>
        <p className="text-sm text-content-secondary">BSP Environmental Stewardship — Objective Registry, KPI Scorecards, and AI Copilot</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {TABS.map(t => {
          const Icon = TAB_ICONS[t.key] || ChartBar;
          return (
            <button key={t.key} data-testid={`env-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                tab === t.key ? 'bg-brand-primary text-white' : 'bg-white text-content-secondary hover:bg-surface-muted border border-border-default'}`}>
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {tab === 'scorecard' && <ScorecardTab objectives={objectives} summary={summary} onDrill={setDrillKey} />}
      {tab === 'facilities' && <FacilitiesTab data={facilitiesData} onDrill={setDrillKey} />}
      {tab === 'procurement' && <ProcurementTab data={procurementData} onDrill={setDrillKey} />}
      {tab === 'resilience' && <ResilienceTab data={resilienceData} onDrill={setDrillKey} />}
      {tab === 'reserves' && <ReservesTab data={reservesData} onDrill={setDrillKey} />}
      {tab === 'report' && <AnnualReportStudio />}
      {tab === 'evidence' && <EvidenceHub objectives={objectives} />}
      {tab === 'author' && <ObjectiveAuthor onSaved={() => { api.get('/api/env/dashboard').then(r => setDashboard(r.data)); }} />}

      {/* Objective Registry */}
      <div className="bg-white border border-border-default rounded-lg p-4">
        <h3 className="font-heading font-semibold text-sm text-content-primary mb-3">Objective Registry ({objectives.length} objectives)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {objectives.map(o => (
            <ObjectiveCard key={o.objective_id} obj={o} onClick={() => setExpandedObj(expandedObj === o.objective_id ? null : o.objective_id)} onDrill={setDrillKey} />
          ))}
        </div>
      </div>

      {/* Objective Detail Drill-Down */}
      {expandedObj && (
        <ObjectiveDetailPanel objectiveId={expandedObj} onClose={() => setExpandedObj(null)} />
      )}

      {/* AI Copilot */}
      <AiCopilotPanel objectives={objectives} />

      <DrillDownModal isOpen={!!drillKey} onClose={() => setDrillKey(null)} metricKey={drillKey} />
    </div>
  );
}
